/**
 * ContactDetailSlideover — iter 508j (Dawn 2026-02-25)
 *
 * The canonical "who is this?" detail panel. Slides in from the right
 * over any surface (Comms, Client File, Dashboard, Healthcare pages).
 *
 * What it shows
 * -------------
 *   Header ribbon:
 *     • Name + contact-type badge (colored per type)
 *     • Phone (call button) + fax + email
 *     • Favorite star toggle
 *     • Edit / Merge / Close controls
 *
 *   Body:
 *     • Cross-channel Timeline (SMS + calls + VMs + faxes + emails)
 *     • Channel filter chips at the top of the timeline
 *
 * Wiring
 * ------
 * Consumed via the `useContactSlideover()` hook from any component:
 *
 *   const { open } = useContactSlideover();
 *   <button onClick={() => open({ contactId: c.id })}>Details</button>
 *
 *   // Or by phone number when we don't have a contact_id (unknown
 *   // numbers, inbound-only rows):
 *   <button onClick={() => open({ phone: "+14145551234" })}>Details</button>
 *
 * The provider (mounted once in MainLayout) owns the visibility state
 * and renders exactly ONE slideover for the whole app so we can never
 * end up with two panels stacked on top of each other. Registers with
 * the existing slideoverStore so the floating Ask-Avi FAB auto-hides.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { X, Phone, MessageSquare, Star, Pencil, Loader2, ExternalLink, Building2 } from "lucide-react";
import { registerSlideover } from "../../lib/slideoverStore";
import CommsTimelinePanel from "../healthcare/CommsTimelinePanel";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Type badge colors — mirrors the LEFT_ACCENT map in ContactsTab.
const TYPE_COLOR = {
  client:      { bg: "#EEF4FF", fg: "#1E40AF", label: "Client" },
  caregiver:   { bg: "#ECFDF5", fg: "#065F46", label: "Caregiver" },
  staff:       { bg: "#EEF2FF", fg: "#3730A3", label: "Staff" },
  team:        { bg: "#EEF2FF", fg: "#3730A3", label: "Team" },
  provider:    { bg: "#FEF3C7", fg: "#92400E", label: "Provider" },
  clinic:      { bg: "#FEF3C7", fg: "#92400E", label: "Clinic" },
  guardian:    { bg: "#FCE7F3", fg: "#9D174D", label: "Family" },
  family_contact: { bg: "#FCE7F3", fg: "#9D174D", label: "Family" },
  emergency_contact: { bg: "#FEE2E2", fg: "#991B1B", label: "Emergency" },
  mco:         { bg: "#F1F5F9", fg: "#334155", label: "MCO" },
  other:       { bg: "#F1F5F9", fg: "#475569", label: "Contact" },
};

function formatPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1")) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || "";
}

// ────────────────────────────────────────────────────────────────
// Context + hook
// ────────────────────────────────────────────────────────────────
const ContactSlideoverCtx = createContext(null);

export function useContactSlideover() {
  const ctx = useContext(ContactSlideoverCtx);
  if (!ctx) {
    // Fail loudly in dev — a bare `useContactSlideover()` call means
    // the provider isn't mounted, which is a wiring bug we want to
    // catch, not swallow.
    console.error("useContactSlideover() called outside <ContactSlideoverProvider>");
    return {
      open: () => {},
      close: () => {},
      isOpen: false,
    };
  }
  return ctx;
}

// ────────────────────────────────────────────────────────────────
// Provider — mount once in MainLayout
// ────────────────────────────────────────────────────────────────
export function ContactSlideoverProvider({ children }) {
  const [target, setTarget] = useState(null); // { contactId? | phone? }

  const open = useCallback((t) => {
    if (!t || (!t.contactId && !t.phone)) return;
    setTarget(t);
  }, []);
  const close = useCallback(() => setTarget(null), []);

  // Register with slideoverStore so the Ask-Avi FAB hides itself.
  useEffect(() => {
    if (!target) return undefined;
    return registerSlideover("contact-detail", true);
  }, [target]);

  // Escape closes.
  useEffect(() => {
    if (!target) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, close]);

  const value = useMemo(
    () => ({ open, close, isOpen: !!target }),
    [open, close, target],
  );

  return (
    <ContactSlideoverCtx.Provider value={value}>
      {children}
      {target && (
        <ContactDetailSlideover
          contactId={target.contactId}
          phone={target.phone}
          onClose={close}
        />
      )}
    </ContactSlideoverCtx.Provider>
  );
}

// ────────────────────────────────────────────────────────────────
// Slideover UI
// ────────────────────────────────────────────────────────────────
function ContactDetailSlideover({ contactId, phone, onClose }) {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr("");
      // Try contact_id first if provided; on 404, fall back to
      // phone-number lookup. The Call Log's r.contact_id can be stale
      // (contact merged/deleted server-side) so a hard-coded id no
      // longer matches — falling through to /by-phone keeps the panel
      // useful instead of showing "Unknown number".
      const tryFetch = async () => {
        if (contactId) {
          const res = await fetch(`${API}/api/contacts/${contactId}`, { headers: authHdr() });
          if (res.status !== 404) {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return await res.json();
          }
        }
        // No id, or id 404'd. Fall through to phone lookup.
        const phoneToUse = phone
          || (contactId && null); // no derivation available
        if (!phoneToUse) return { id: null, phone_number: phone, unknown: true };
        const enc = encodeURIComponent(phoneToUse);
        const res2 = await fetch(`${API}/api/contacts/by-phone?phone=${enc}`, { headers: authHdr() });
        if (res2.status === 404) return { id: null, phone_number: phoneToUse, unknown: true };
        if (!res2.ok) throw new Error(`HTTP ${res2.status}`);
        const data = await res2.json();
        // /by-phone returns { items: [...], tail, total }. Take the
        // first hit (loose match on the last 10 digits) or fall back
        // to the "unknown" card so the timeline can still open.
        const items = Array.isArray(data) ? data : (data.items || []);
        if (items.length === 0) return { id: null, phone_number: phoneToUse, unknown: true };
        return items[0];
      };

      try {
        const data = await tryFetch();
        if (alive) setContact(data);
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load contact");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [contactId, phone]);

  const displayName = (() => {
    if (!contact) return "";
    if (contact.unknown) return "Unknown number";
    const n = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
    return n || formatPhone(contact.phone_number || contact.phone || "") || "Unnamed contact";
  })();

  const type = (contact?.contact_type || "").toLowerCase();
  const meta = TYPE_COLOR[type] || TYPE_COLOR.other;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-[70] bg-slate-900/30 backdrop-blur-[1px]"
        data-testid="contact-detail-backdrop"
      />
      {/* Panel */}
      <aside
        className="fixed top-0 right-0 z-[71] h-full w-full sm:w-[460px] bg-white shadow-2xl flex flex-col"
        data-testid="contact-detail-slideover"
        role="dialog"
        aria-label="Contact details"
      >
        {/* Header */}
        <div className="border-b p-4 flex items-start gap-3" style={{ borderColor: "#E2E8F0" }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[15px] font-black text-slate-900 truncate" data-testid="contact-detail-name">
                {displayName || (loading ? "…" : "")}
              </h2>
              {!contact?.unknown && (
                <span
                  className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
                  style={{ background: meta.bg, color: meta.fg }}
                  data-testid="contact-detail-type-badge"
                >
                  {meta.label}
                </span>
              )}
              {contact?.is_favorite && (
                <Star className="w-3.5 h-3.5" style={{ color: "#F59E0B", fill: "#F59E0B" }} />
              )}
            </div>
            {(contact?.phone_number || phone) && (
              <div className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-700 font-mono">
                <Phone className="w-3.5 h-3.5 text-slate-400" />
                {formatPhone(contact?.phone_number || phone)}
              </div>
            )}
            {contact?.email && (
              <div className="text-[12px] text-slate-500 mt-0.5 truncate">{contact.email}</div>
            )}
            {(contact?.practice_name || contact?.company) && (
              <div className="mt-1 flex items-center gap-1.5 text-[12px] text-slate-600">
                <Building2 className="w-3 h-3 text-slate-400" />
                {contact.practice_name || contact.company}
              </div>
            )}
          </div>
          {/* Actions */}
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-slate-100"
              aria-label="Close"
              data-testid="contact-detail-close"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
            {contact?.id && !contact.unknown && (
              <a
                href={`/contacts?legacy=1&edit=${encodeURIComponent(contact.id)}`}
                className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="contact-detail-edit"
              >
                <Pencil className="w-3 h-3" /> Edit
              </a>
            )}
          </div>
        </div>

        {/* Quick actions */}
        {(contact?.phone_number || phone) && (
          <div className="border-b bg-slate-50 px-4 py-2 flex items-center gap-2" style={{ borderColor: "#E2E8F0" }}>
            <a
              href={`/phone?tab=texts&who=${encodeURIComponent(contact?.phone_number || phone)}`}
              className="h-8 px-2.5 rounded-md text-white text-[11.5px] font-semibold flex items-center gap-1.5 hover:brightness-95"
              style={{ background: "#10B981" }}
              data-testid="contact-detail-text"
            >
              <MessageSquare className="w-3.5 h-3.5" /> Text
            </a>
            <a
              href={`tel:${(contact?.phone_number || phone || "").replace(/[^+\d]/g, "")}`}
              className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium flex items-center gap-1.5 hover:bg-white"
              style={{ borderColor: "#CBD5E1", color: "#0F172A" }}
              data-testid="contact-detail-call"
            >
              <Phone className="w-3.5 h-3.5" /> Call
            </a>
            {contact?.unknown && (
              <span
                className="ml-auto text-[11px] italic text-slate-500 inline-flex items-center gap-1"
                data-testid="contact-detail-unknown-hint"
              >
                <ExternalLink className="w-3 h-3" />
                Not in your contact registry
              </span>
            )}
          </div>
        )}

        {/* Body: timeline */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading && (
            <div className="p-6 text-center text-[12px] text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading contact…
            </div>
          )}
          {err && !loading && (
            <div className="p-6 text-center text-[12px] text-red-600" data-testid="contact-detail-error">
              {err}
            </div>
          )}
          {!loading && !err && contact?.id && !contact.unknown && (
            <CommsTimelinePanel contactId={contact.id} days={90} />
          )}
          {!loading && !err && contact?.unknown && (
            <div className="p-6 text-center text-[12px] text-slate-500">
              <p className="mb-2">
                This number isn&apos;t saved yet, so we can&apos;t show a
                cross-channel history.
              </p>
              <a
                href={`/phone?tab=texts&who=${encodeURIComponent(phone)}`}
                className="text-[12px] font-semibold text-blue-700 hover:underline"
              >
                Open the text thread →
              </a>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
